// LinkedWishPanel — cross-module reference for DiaryDetail (W35.2).
//
// Per docs/spec/wishlist/W35.2-SUBRESOURCE-UI-POLISH.md §3.1:
// - shows when diary.source_wish_id is set (created via complete-with-diary)
// - fetches the wish via the typed wishlist client + renders a compact
//   card with chevron linking to /wishes/{id}
//
// Sized for the AGENTS.md §3 #10 sub-component budget (~2KB).

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { getWish } from "@/lib/api/wishlist";
import { wishStatusLabel } from "@/lib/i18n/wishlist";
import { cn } from "@/lib/utils/cn";

export interface LinkedWishPanelProps {
  wishId: string;
  className?: string;
}

export function LinkedWishPanel({
  wishId,
  className,
}: LinkedWishPanelProps) {
  const { data: wish } = useQuery({
    queryKey: ["wishes", "detail", wishId],
    queryFn: () => getWish(wishId),
    enabled: Boolean(wishId),
    staleTime: 30_000,
  });

  if (!wish) {
    return null;
  }

  return (
    <Card
      data-component="LinkedWishPanel"
      data-testid="linked-wish-panel"
      className={cn("p-s-4", className)}
    >
      <header className="flex items-center gap-2 mb-s-2">
        <Sparkles className="h-4 w-4" aria-hidden />
        <h3 className="font-display text-h3 text-ink-1">連結的心願</h3>
      </header>
      <Link
        to={`/wishes/${wish.id}`}
        data-testid="linked-wish-link"
        className="press flex items-center justify-between gap-2 p-s-3 rounded-md bg-paper-deep hover:bg-paper-calm text-ink-1"
      >
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-body-sm truncate">{wish.name}</p>
          <p className="text-meta text-ink-3 tabular-nums">
            {/* W53.1 D-6: this rendered the raw enum — users saw the literal
                string `pending` on a zh-Hant screen. `lib/i18n/dashboard.ts`
                exists so that class of bug cannot recur, and it recurred here,
                in the next file nobody connected to that rule. Guard G-2 reads
                JSX for `wish.status` so it cannot come back quietly. */}
            進度 {wish.progress_percent}% · 狀態 {wishStatusLabel(wish.status)}
          </p>
        </div>
        <ChevronRight className="h-4 w-4 text-ink-3 shrink-0" aria-hidden />
      </Link>
    </Card>
  );
}